import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"
import type { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  formControlLengthClasses,
  type FormControlLength,
} from "@/components/ui/form-control-length"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/**
 * design-system.md's `DateRangePicker` — a `Popover` + `Calendar(mode="range")`
 * composition, ported from `otk/wireframes`' own `DateRangePicker`. Picking a
 * single day sets both ends of the range to that day. The trailing "×" button
 * only renders once a date is picked and calls the same `onChange` the
 * calendar itself uses, with `undefined`.
 */
export function DateRangePicker({
  value,
  onChange,
  placeholder,
  className,
  length,
}: {
  value: DateRange | undefined
  onChange: (range: DateRange | undefined) => void
  placeholder: string
  className?: string
  length?: FormControlLength
}) {
  const isSingleDay =
    !value?.to ||
    (value.from &&
      format(value.to, "yyyy-MM-dd") === format(value.from, "yyyy-MM-dd"))
  const label = value?.from
    ? isSingleDay
      ? format(value.from, "dd.MM.yyyy")
      : `${format(value.from, "dd.MM.yyyy")} – ${format(value.to!, "dd.MM.yyyy")}`
    : placeholder

  return (
    <div
      className={cn(
        "flex items-center gap-1",
        length && formControlLengthClasses[length],
        className
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button className="flex-1" variant="outline">
            <CalendarIcon data-icon="inline-start" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="range"
            numberOfMonths={2}
            onSelect={onChange}
            selected={value}
          />
        </PopoverContent>
      </Popover>
      {value?.from && (
        <Button
          aria-label="Очистити дату"
          onClick={() => onChange(undefined)}
          size="icon-sm"
          variant="ghost"
        >
          <X />
        </Button>
      )}
    </div>
  )
}
